import React from "react"

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@coval/ui"

interface DeleteRunsModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  itemCount: number
  isDeleting?: boolean
  failedRunIds?: string[]
  /** Overrides the default "Confirm Deletion" title — use for a single, named subject (e.g. one run). */
  title?: string
  /** Overrides the default "delete {itemCount} run(s)" sentence — use when itemCount alone doesn't disambiguate the subject. */
  description?: React.ReactNode
  /** Overrides the default "Delete {itemCount} Item(s)" confirm button label. */
  confirmLabel?: string
}

export const DeleteRunsModal = ({
  isOpen,
  onClose,
  onConfirm,
  itemCount,
  isDeleting,
  failedRunIds = [],
  title = "Confirm Deletion",
  description,
  confirmLabel,
}: DeleteRunsModalProps) => (
  <Dialog
    open={isOpen}
    onOpenChange={(open) => !open && !isDeleting && onClose()}
  >
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <p>
          {description ??
            `Are you sure you want to delete ${itemCount} run(s)?`}
        </p>
        <p className="text-sm text-coval-danger-text">
          This action cannot be undone.
        </p>
        {failedRunIds.length > 0 && (
          <div
            className="rounded-md border border-coval-danger-border/30 bg-coval-danger-surface p-3"
            aria-live="polite"
          >
            <p className="text-sm font-medium text-coval-danger-on-surface">
              Failed to delete {failedRunIds.length} run
              {failedRunIds.length === 1 ? "" : "s"}.
            </p>
            <p className="mt-1 text-sm text-coval-danger-on-surface">
              Review the failed run IDs below and retry only those runs.
            </p>
            <ul className="mt-2 max-h-32 overflow-auto text-xs text-coval-danger-on-surface">
              {failedRunIds.map((runId) => (
                <li key={runId}>{runId}</li>
              ))}
            </ul>
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={onConfirm}
          loading={isDeleting}
          data-testid="run-delete-confirm-button"
        >
          {confirmLabel ??
            `Delete ${itemCount} Item${itemCount === 1 ? "" : "s"}`}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
)
